import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle2, AlertTriangle, XCircle, AlertCircle, 
  TrendingUp, Shield, Calculator, FileSpreadsheet, 
  Wallet, BarChart3, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const CheckItem = ({ status, label, message, detail }) => {
  const icons = {
    pass: <CheckCircle2 className="w-4 h-4 text-emerald-400" />,
    warn: <AlertTriangle className="w-4 h-4 text-amber-400" />,
    fail: <XCircle className="w-4 h-4 text-red-400" />,
    info: <AlertCircle className="w-4 h-4 text-indigo-400" />,
  };
  const colors = {
    pass: 'border-emerald-500/20 bg-emerald-500/5',
    warn: 'border-amber-500/20 bg-amber-500/5',
    fail: 'border-red-500/20 bg-red-500/5',
    info: 'border-indigo-500/20 bg-indigo-500/5',
  };

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${colors[status]}`}>
      <div className="shrink-0 mt-0.5">{icons[status]}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-zinc-200">{label}</span>
          <Badge variant="outline" className={`text-[9px] ${
            status === 'pass' ? 'border-emerald-500/30 text-emerald-400' :
            status === 'warn' ? 'border-amber-500/30 text-amber-400' :
            status === 'fail' ? 'border-red-500/30 text-red-400' :
            'border-indigo-500/30 text-indigo-400'
          }`}>
            {status === 'pass' ? 'PASS' : status === 'warn' ? 'WARNING' : status === 'fail' ? 'FAIL' : 'INFO'}
          </Badge>
        </div>
        <p className="text-xs text-zinc-400 mt-0.5">{message}</p>
        {detail && <p className="text-[10px] text-zinc-500 mt-1 font-mono">{detail}</p>}
      </div>
    </div>
  );
};

const CategorySection = ({ icon: Icon, title, checks, color = 'indigo' }) => {
  const passCount = checks.filter(c => c.status === 'pass').length;
  const totalCount = checks.length;
  const allPass = passCount === totalCount;

  return (
    <Card className={`bg-zinc-900/60 border-zinc-800/60 ${allPass ? 'border-l-2 border-l-emerald-500' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 text-${color}-400`} />
            <CardTitle className="text-sm font-medium text-zinc-200">{title}</CardTitle>
          </div>
          <Badge variant="outline" className={`text-[10px] ${
            allPass ? 'border-emerald-500/30 text-emerald-400' : 'border-amber-500/30 text-amber-400'
          }`}>
            {passCount}/{totalCount} passed
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {checks.map((check, i) => (
          <CheckItem key={i} {...check} />
        ))}
      </CardContent>
    </Card>
  );
};

export default function ValidationPage({ data, results, analysis, onRecalculate, loading }) {
  const validationResults = useMemo(() => {
    const checks = {
      dataCompleteness: [],
      financialConsistency: [],
      valuationHealth: [],
      dealStructure: [],
      riskIndicators: [],
    };

    // === DATA COMPLETENESS ===
    // Check if P&L data exists
    const pnl = results?.historical_pnl || [];
    if (pnl.length >= 2) {
      checks.dataCompleteness.push({
        status: 'pass',
        label: 'P&L Data Present',
        message: `${pnl.length} years of financial data loaded`,
        detail: `Years: ${pnl.map(p => p.year).join(', ')}`,
      });
    } else if (pnl.length === 1) {
      checks.dataCompleteness.push({
        status: 'warn',
        label: 'Limited P&L Data',
        message: 'Only 1 year of data — trend analysis limited',
        detail: 'Recommend at least 2-3 years for meaningful trends',
      });
    } else {
      checks.dataCompleteness.push({
        status: 'fail',
        label: 'P&L Data Missing',
        message: 'No historical P&L data found',
        detail: 'Import data or enter manually via Data Import',
      });
    }

    // Check SDE data
    const sde = results?.sde_analysis || [];
    if (sde.length > 0 && sde.some(s => s.sde > 0)) {
      checks.dataCompleteness.push({
        status: 'pass',
        label: 'SDE Calculated',
        message: 'Seller\'s Discretionary Earnings computed',
        detail: `Latest SDE: $${sde[sde.length - 1]?.sde?.toLocaleString() || 'N/A'}`,
      });
    } else {
      checks.dataCompleteness.push({
        status: 'warn',
        label: 'SDE Not Calculated',
        message: 'SDE add-backs may be missing or zero',
        detail: 'Check SDE Calculator tab for add-back entries',
      });
    }

    // Check balance sheet
    const solvency = results?.solvency;
    if (solvency?.buyer?.current_ratio > 0) {
      checks.dataCompleteness.push({
        status: 'pass',
        label: 'Balance Sheet Present',
        message: 'Solvency ratios calculable',
      });
    } else {
      checks.dataCompleteness.push({
        status: 'warn',
        label: 'Balance Sheet Incomplete',
        message: 'Current ratio unavailable — balance sheet may be missing',
        detail: 'Enter balance sheet data for solvency analysis',
      });
    }

    // Check DCF
    const dcf = results?.dcf;
    if (dcf?.enterprise_value > 0) {
      checks.dataCompleteness.push({
        status: 'pass',
        label: 'DCF Valuation Complete',
        message: 'Enterprise value calculated',
        detail: `EV: $${dcf.enterprise_value?.toLocaleString()}`,
      });
    } else {
      checks.dataCompleteness.push({
        status: 'fail',
        label: 'DCF Valuation Missing',
        message: 'No enterprise value calculated',
        detail: 'Check DCF assumptions and base revenue',
      });
    }

    // Check deal scenarios
    const deals = results?.deal_comparison;
    if (deals?.seller && deals?.buyer) {
      checks.dataCompleteness.push({
        status: 'pass',
        label: 'Deal Scenarios Defined',
        message: 'Seller and buyer deals configured',
      });
    } else {
      checks.dataCompleteness.push({
        status: 'warn',
        label: 'Deal Scenarios Incomplete',
        message: 'Missing seller or buyer deal terms',
      });
    }

    // === FINANCIAL CONSISTENCY ===
    // P&L math check
    if (pnl.length > 0) {
      const latest = pnl[pnl.length - 1];
      const calcGross = latest.revenue + latest.cogs; // cogs is negative
      const grossDiff = Math.abs(calcGross - latest.gross_profit);
      if (grossDiff < 1) {
        checks.financialConsistency.push({
          status: 'pass',
          label: 'Gross Profit Calculation',
          message: 'Revenue - COGS = Gross Profit ✓',
        });
      } else {
        checks.financialConsistency.push({
          status: 'warn',
          label: 'Gross Profit Discrepancy',
          message: `Calculated gross profit differs by $${grossDiff.toLocaleString()}`,
          detail: 'May be due to rounding or data entry error',
        });
      }

      // Margin sanity check
      if (latest.gross_margin >= 0 && latest.gross_margin <= 1) {
        checks.financialConsistency.push({
          status: 'pass',
          label: 'Margin Range Valid',
          message: `Gross margin ${(latest.gross_margin * 100).toFixed(1)}% is within expected range`,
        });
      } else {
        checks.financialConsistency.push({
          status: 'fail',
          label: 'Invalid Margin',
          message: 'Gross margin outside 0-100% range',
          detail: 'Check revenue and COGS signs (COGS should be negative)',
        });
      }
    }

    // Deal financing check
    if (deals?.buyer) {
      const buyer = deals.buyer;
      const totalPct = (data?.buyer_deal?.sba_loan_pct || 0) + 
                       (data?.buyer_deal?.seller_note_pct || 0) + 
                       (data?.buyer_deal?.equity_pct || 0);
      if (Math.abs(totalPct - 1) < 0.01) {
        checks.financialConsistency.push({
          status: 'pass',
          label: 'Financing Structure',
          message: 'SBA + Seller Note + Equity = 100%',
        });
      } else {
        checks.financialConsistency.push({
          status: 'warn',
          label: 'Financing Imbalance',
          message: `Total financing is ${(totalPct * 100).toFixed(0)}%, not 100%`,
          detail: 'Check SBA, seller note, and equity percentages',
        });
      }
    }

    // === VALUATION HEALTH ===
    if (dcf?.enterprise_value > 0 && deals?.seller?.purchase_price > 0) {
      const evVsAsking = dcf.enterprise_value / deals.seller.purchase_price;
      if (evVsAsking >= 1.5) {
        checks.valuationHealth.push({
          status: 'pass',
          label: 'Strong Valuation Support',
          message: `DCF value ${evVsAsking.toFixed(1)}x the asking price`,
          detail: 'Significant margin of safety',
        });
      } else if (evVsAsking >= 1.0) {
        checks.valuationHealth.push({
          status: 'pass',
          label: 'Adequate Valuation Support',
          message: `DCF value ${evVsAsking.toFixed(2)}x the asking price`,
        });
      } else if (evVsAsking >= 0.8) {
        checks.valuationHealth.push({
          status: 'warn',
          label: 'Valuation Concern',
          message: `DCF value only ${(evVsAsking * 100).toFixed(0)}% of asking price`,
          detail: 'Consider negotiating price down or revising assumptions',
        });
      } else {
        checks.valuationHealth.push({
          status: 'fail',
          label: 'Valuation Red Flag',
          message: `DCF value significantly below asking (${(evVsAsking * 100).toFixed(0)}%)`,
          detail: 'Deal may be overpriced or assumptions too conservative',
        });
      }
    }

    // SDE Multiple check
    if (deals?.buyer?.sde_multiple > 0) {
      const mult = deals.buyer.sde_multiple;
      if (mult <= 3.5) {
        checks.valuationHealth.push({
          status: 'pass',
          label: 'SDE Multiple Reasonable',
          message: `${mult.toFixed(2)}x is typical for SMB acquisitions`,
        });
      } else if (mult <= 5) {
        checks.valuationHealth.push({
          status: 'warn',
          label: 'SDE Multiple Elevated',
          message: `${mult.toFixed(2)}x is on the higher end`,
          detail: 'Ensure growth prospects justify premium',
        });
      } else {
        checks.valuationHealth.push({
          status: 'fail',
          label: 'SDE Multiple Too High',
          message: `${mult.toFixed(2)}x exceeds typical SMB range`,
          detail: 'Very high risk — reconsider deal terms',
        });
      }
    }

    // === DEAL STRUCTURE ===
    if (deals?.buyer?.dscr > 0) {
      const dscr = deals.buyer.dscr;
      if (dscr >= 1.5) {
        checks.dealStructure.push({
          status: 'pass',
          label: 'Strong DSCR',
          message: `${dscr.toFixed(2)}x provides comfortable debt coverage`,
        });
      } else if (dscr >= 1.35) {
        checks.dealStructure.push({
          status: 'pass',
          label: 'Adequate DSCR',
          message: `${dscr.toFixed(2)}x meets SBA minimum threshold`,
        });
      } else if (dscr >= 1.0) {
        checks.dealStructure.push({
          status: 'warn',
          label: 'Low DSCR',
          message: `${dscr.toFixed(2)}x below SBA 1.35x target`,
          detail: 'May struggle to qualify for SBA financing',
        });
      } else {
        checks.dealStructure.push({
          status: 'fail',
          label: 'Insufficient DSCR',
          message: `${dscr.toFixed(2)}x — debt service exceeds cash flow`,
          detail: 'Deal structure unsustainable without changes',
        });
      }
    }

    // FCF check
    if (deals?.buyer?.fcf_to_buyer !== undefined) {
      const fcf = deals.buyer.fcf_to_buyer;
      if (fcf > 50000) {
        checks.dealStructure.push({
          status: 'pass',
          label: 'Positive Cash Flow',
          message: `$${fcf.toLocaleString()} annual FCF to buyer`,
        });
      } else if (fcf > 0) {
        checks.dealStructure.push({
          status: 'warn',
          label: 'Marginal Cash Flow',
          message: `$${fcf.toLocaleString()} annual FCF — thin margin`,
          detail: 'Limited buffer for unexpected expenses',
        });
      } else {
        checks.dealStructure.push({
          status: 'fail',
          label: 'Negative Cash Flow',
          message: `$${fcf.toLocaleString()} — deal loses money`,
          detail: 'Restructure financing or reduce purchase price',
        });
      }
    }

    // === RISK INDICATORS ===
    const flags = results?.trend_flags || [];
    const negFlags = flags.filter(f => f.type === 'negative');
    const posFlags = flags.filter(f => f.type === 'positive');

    if (negFlags.length === 0) {
      checks.riskIndicators.push({
        status: 'pass',
        label: 'No Red Flags',
        message: 'No negative trend indicators detected',
      });
    } else if (negFlags.length <= 2) {
      checks.riskIndicators.push({
        status: 'warn',
        label: 'Minor Concerns',
        message: `${negFlags.length} negative indicator(s) found`,
        detail: negFlags.map(f => f.message).join('; '),
      });
    } else {
      checks.riskIndicators.push({
        status: 'fail',
        label: 'Multiple Red Flags',
        message: `${negFlags.length} negative indicators detected`,
        detail: 'Review Key Findings for details',
      });
    }

    // Revenue trend
    if (pnl.length >= 2) {
      const latest = pnl[pnl.length - 1];
      const yoy = latest.yoy_growth;
      if (yoy !== null && yoy !== undefined) {
        if (yoy > 0.05) {
          checks.riskIndicators.push({
            status: 'pass',
            label: 'Revenue Growing',
            message: `${(yoy * 100).toFixed(1)}% YoY growth`,
          });
        } else if (yoy >= -0.05) {
          checks.riskIndicators.push({
            status: 'warn',
            label: 'Revenue Flat',
            message: `${(yoy * 100).toFixed(1)}% YoY — essentially flat`,
          });
        } else {
          checks.riskIndicators.push({
            status: 'fail',
            label: 'Revenue Declining',
            message: `${(yoy * 100).toFixed(1)}% YoY decline`,
            detail: 'Understand drivers before proceeding',
          });
        }
      }
    }

    // Deal Score summary
    const dealScore = results?.deal_score;
    if (dealScore) {
      if (dealScore.score >= 70) {
        checks.riskIndicators.push({
          status: 'pass',
          label: 'Strong Deal Score',
          message: `${dealScore.score}/100 (${dealScore.grade}) — ${dealScore.assessment}`,
        });
      } else if (dealScore.score >= 55) {
        checks.riskIndicators.push({
          status: 'warn',
          label: 'Moderate Deal Score',
          message: `${dealScore.score}/100 (${dealScore.grade}) — ${dealScore.assessment}`,
        });
      } else {
        checks.riskIndicators.push({
          status: 'fail',
          label: 'Low Deal Score',
          message: `${dealScore.score}/100 (${dealScore.grade}) — ${dealScore.assessment}`,
        });
      }
    }

    return checks;
  }, [data, results]);

  // Calculate overall summary
  const allChecks = Object.values(validationResults).flat();
  const passCount = allChecks.filter(c => c.status === 'pass').length;
  const warnCount = allChecks.filter(c => c.status === 'warn').length;
  const failCount = allChecks.filter(c => c.status === 'fail').length;
  const totalCount = allChecks.length;
  const overallScore = Math.round((passCount / totalCount) * 100);

  const overallStatus = failCount > 2 ? 'fail' : failCount > 0 || warnCount > 3 ? 'warn' : 'pass';
  const statusColors = {
    pass: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30',
    warn: 'from-amber-500/20 to-amber-500/5 border-amber-500/30',
    fail: 'from-red-500/20 to-red-500/5 border-red-500/30',
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="validation-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Model Validation</h2>
          <p className="text-sm text-zinc-400 mt-1">Cross-check data completeness, consistency, and deal health</p>
        </div>
        <Button
          onClick={onRecalculate}
          disabled={loading}
          variant="outline"
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          data-testid="rerun-validation"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Recalculate
        </Button>
      </div>

      {/* Overall Summary */}
      <Card className={`bg-gradient-to-r ${statusColors[overallStatus]} border`}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-zinc-100 mb-1">
                {overallStatus === 'pass' ? 'Model Looks Good' : 
                 overallStatus === 'warn' ? 'Review Recommended' : 'Issues Detected'}
              </h3>
              <p className="text-sm text-zinc-400">
                {passCount} passed, {warnCount} warnings, {failCount} failed out of {totalCount} checks
              </p>
            </div>
            <div className="text-right">
              <div className={`text-4xl font-bold font-mono ${
                overallStatus === 'pass' ? 'text-emerald-400' :
                overallStatus === 'warn' ? 'text-amber-400' : 'text-red-400'
              }`}>
                {overallScore}%
              </div>
              <div className="text-xs text-zinc-500">Health Score</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Validation Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CategorySection 
          icon={FileSpreadsheet} 
          title="Data Completeness" 
          checks={validationResults.dataCompleteness}
          color="indigo"
        />
        <CategorySection 
          icon={Calculator} 
          title="Financial Consistency" 
          checks={validationResults.financialConsistency}
          color="purple"
        />
        <CategorySection 
          icon={TrendingUp} 
          title="Valuation Health" 
          checks={validationResults.valuationHealth}
          color="emerald"
        />
        <CategorySection 
          icon={Wallet} 
          title="Deal Structure" 
          checks={validationResults.dealStructure}
          color="amber"
        />
      </div>

      {/* Risk Indicators - Full Width */}
      <CategorySection 
        icon={Shield} 
        title="Risk Indicators" 
        checks={validationResults.riskIndicators}
        color="red"
      />

      {/* Quick Actions */}
      {failCount > 0 && (
        <Card className="bg-zinc-900/60 border-red-500/20">
          <CardContent className="p-4">
            <h4 className="text-sm font-medium text-zinc-200 mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              Recommended Actions
            </h4>
            <ul className="space-y-1 text-xs text-zinc-400">
              {failCount > 0 && (
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                  Address {failCount} failed check(s) before proceeding with the deal
                </li>
              )}
              {warnCount > 2 && (
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  Review {warnCount} warnings — some may require attention
                </li>
              )}
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                Run "Recalculate" after making changes to update validation
              </li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
